// IoT-Geräte-Store für die Geräte-Seite (/zuhause/geraete). Port des
// home-dash-Stores (devices.svelte.ts), gleiche Wire-Form — nur der
// API-Namespace ist /v2-api/iot/devices (/v2-api/devices gehört den
// Push-Auth-Browser-Geräten). Read-only bis auf die Overrides
// (Label/Bereich); die Sicht baut das Backend live aus den MQTT-Quellen.
//
// Live-Update (Realtime-Mandat) über das Homelab-Protokoll (docs/protokoll.md
// › „Geräte über das Protokoll“, seit 02.11.2026): jedes Gerät ist eine
// Instanz des Topics `geraet.<slug>.stand`. Das Backend baut die Sicht einmal
// je Signal und setzt die Instanzen; der Hub verwirft Unverändertes und
// schickt Deltas. Bis dahin holte jeder Tab bei jedem Signal (~10/min) die
// ganze Liste neu, 47 kB je Abruf.
//   - REST `/v2-api/iot/devices` beim Start, als Revalidator, nach einem
//     Fehler beim Umbenennen und in refreshDevices (Skripte und Seite
//     brauchen weiter die ganze Liste).
//   - `geraet.*.stand` (Rate normal): Snapshot + Delta je Gerät. `stand`
//     aktualisiert das Gerät in der Liste in-place (Objekt-Identität und
//     Reihenfolge bleiben), ein unbekanntes Gerät hängt sich an, `weg` nimmt
//     es heraus. Der jüngste Stand je Gerät wird gemerkt; kam er während
//     eines REST-Abrufs, liegt er über dessen Antwort (sie kann dahinter
//     liegen), ältere nicht.
// Der alte WS-Kanal "devices" bleibt (DeviceHistory.svelte hört darauf).
import { getJSON, postJSON } from "./api";
import { geraetAusProtokoll, slugAusInstanz, uebernimm } from "./iot-devices-protokoll";
import { abonniere } from "./protokoll/client.svelte";
import type { GeraetStand } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";

export interface DeviceEntity {
  slug: string;
  name: string;
  kind: string; // sensor | availability
  unit: string;
  state: string;
  deviceClass: string;
  haEntityId: string; // extID, z.B. "zigbee:0x…:state"
  updatedAt: string;
}

export interface Device {
  slug: string;
  name: string; // Quell-Name (z2m friendly, zwave-Key, esphome-ID)
  label: string; // v2-Override ("" = keiner)
  area: string; // v2-Override, freies Bereichs-Feld
  kind: string;
  manufacturer: string;
  model: string;
  integration: string;
  integrationSlug: string;
  /** Domain fuer entity_disables ("" = Integration kennt keine
   *  Stilllegung). Kommt vom Server -- nicht selbst aus der Adresse
   *  ableiten, siehe devices.EsphomeDisableDomain. */
  disableDomain?: string;
  /** ID der Stilllegung. Bei Zigbee die ieee-Adresse, NICHT der
   *  Anzeigename -- ein z2m-Rename liesse sie sonst verwaisen. */
  disableEntity?: string;
  /** Rechner, den diese Steckdose mit Strom versorgt (v2-Override). */
  versorgt?: string;
  /** Versorgt den Rechner des Dashboards: Ausschalten sperrt der Server. */
  ausGesperrt?: boolean;
  haDeviceId: string; // Quell-Adresse (ieee, "Node 4", "co2monitor:x")
  online: boolean | null;
  // Zigbee-Funkqualitaet (0..255), reine Live-Diagnose (keine Entitaet).
  lqi?: number; // null = Quelle meldet nichts -> Freshness entscheidet
  lastSeen: string; // RFC3339 oder ""
  /** Das Board hat (auch) die Rolle Funkmelder -- Bewegung aus dem
   *  WLAN-Kanal. `id`: unter der der Melder in /v2-api/funkmelder steht.
   *  `aus`: die Funktion ist abgeschaltet. `abschaltbar`: sie lässt sich
   *  von hier schalten. */
  funkmelder?: { id: string; aus: boolean; abschaltbar: boolean };
  entities: DeviceEntity[];
}

// Anzeigename: Override wenn gesetzt, sonst der Quell-Name.
export function deviceName(d: Device): string {
  return d.label.trim() || d.name;
}

// Entitäts-Beschriftung: Backend liefert bereits deutsche Rollen-Namen
// ("Temperatur", "Schaltzustand"); Geräteanteile abstreifen wie in home
// braucht es hier nicht.
export function entityLabel(_d: Device, e: Pick<DeviceEntity, "name" | "kind" | "slug">): string {
  return e.name || e.slug;
}

// Zustandswörter fürs UI: die Quellen liefern teils englische Rohzustände.
const STATE_LABELS: Record<string, string> = {
  on: "an",
  off: "aus",
  open: "offen",
  closed: "zu",
  online: "online",
  offline: "offline",
  unavailable: "nicht erreichbar",
};
export function stateLabel(state: string): string {
  return STATE_LABELS[state] ?? state;
}

// Erreichbarkeit: explizite Quelle-Meldung (availability/online) zählt;
// null = keine Aussage möglich (Frontend fällt auf Freshness zurück).
export function deviceReachable(d: Device): boolean | null {
  if (d.online !== null && d.online !== undefined) return d.online;
  for (const e of d.entities) {
    if (e.kind === "availability") return e.state !== "offline";
  }
  return null;
}

export const devices = $state<{ list: Device[]; loading: boolean; error: string }>({
  list: [],
  loading: true,
  error: "",
});

let started = false;

// Der jüngste Protokoll-Stand je Gerät (Slug), mit laufender Nummer des
// Eintreffens: ein Abruf legt nur Stände über seine Antwort, die nach seinem
// Start kamen -- ältere hat der Server beim Bauen der Antwort schon gekannt
// (und nach einer Unterbrechung des Protokolls wären sie veraltet). `s: null`
// = das Gerät ist weg: eine Antwort, die es noch nennt, holt es nicht zurück.
const stand = new Map<string, { s: GeraetStand | null; nr: number }>();
let standNr = 0;
// Umbenennung oder Bereich, die der Nutzer eben gesetzt hat und die das
// Backend noch nicht zurückgemeldet hat: ein Stand, der vor dem Speichern
// gebaut wurde, soll den Wert nicht kurz zurücksetzen. Gilt, bis der Stand
// den Wert nennt, die Anfrage scheitert oder OFFEN_MS vorbei sind (ein
// anderer Tab könnte den Wert inzwischen geändert haben).
const OFFEN_MS = 5_000;
const offen = new Map<string, { label?: string; area?: string; bis: number }>();

export function ensureDevices(): void {
  if (started) return;
  started = true;
  void load();
  abonniere("geraet.*.stand", { rate: "normal" }, { stand: beiStand, weg: beiWeg });
  // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reisst die Leitung ab -- und
  // jeder Deploy tut das --, sind die Frames aus der Luecke fuer immer
  // weg. Kommt das Geraet ausgerechnet dann zurueck, steht die Seite
  // bis zum naechsten Reload auf "nicht erreichbar", waehrend das
  // Telefon laengst die Entwarnung hat (Tim 22.08.: "es wird erst
  // wieder online gezeigt, wenn die Seite neu geladen wird").
  //
  // registerRevalidator schliesst genau diese Luecke: der ws-hub feuert
  // beim Reconnect v2:ws-reconnect, und die Revalidation holt alles
  // Registrierte frisch. Wer einen Store per WS aktuell haelt, meldet
  // ihn HIER mit an -- sonst ist er nur solange aktuell, wie die
  // Leitung steht. (Der Protokoll-Client holt sich nach einer Lücke selbst
  // einen Snapshot; die Revalidation deckt zusätzlich den REST-Weg ab.)
  registerRevalidator(load);
}

/** Holt die Liste frisch -- nach einer Aktion, deren Ergebnis die Seite
 *  sofort zeigen soll, ohne auf den WS-Frame zu warten. */
export function refreshDevices(): Promise<void> {
  return load();
}

async function load(): Promise<void> {
  const ab = standNr;
  try {
    const r = await getJSON<{ devices: Device[] }>("/v2-api/iot/devices");
    applyDevices(r.devices ?? [], ab);
    devices.error = "";
  } catch (e) {
    devices.error = e instanceof Error ? e.message : String(e);
    // Was das Protokoll während des Abrufs gebracht hat, gilt trotzdem --
    // beim ersten Abruf wäre die Liste sonst leer, bis jedes Gerät sich ändert.
    applyDevices(devices.list, ab);
  } finally {
    devices.loading = false;
  }
}

// Das Gerät aus dem gemerkten Protokoll-Stand, mit einer noch offenen
// Umbenennung darüber.
function ausStand(slug: string, s: GeraetStand): Device {
  const d = geraetAusProtokoll(slug, s);
  const o = offen.get(slug);
  if (o) {
    if (Date.now() > o.bis) {
      offen.delete(slug);
    } else {
      if (o.label !== undefined) d.label = o.label;
      if (o.area !== undefined) d.area = o.area;
      // Bestätigt, sobald der Stand den Wert trägt.
      if ((s.label ?? "") === o.label) delete o.label;
      if ((s.bereich ?? "") === o.area) delete o.area;
      if (o.label === undefined && o.area === undefined) offen.delete(slug);
    }
  }
  return d;
}

function beiStand(instanz: string, daten: Record<string, unknown>): void {
  const slug = slugAusInstanz(instanz);
  if (!slug) return;
  const s = skaliere<GeraetStand>("geraet.stand", daten);
  stand.set(slug, { s, nr: ++standNr });
  const neu = ausStand(slug, s);
  const ex = devices.list.find((d) => d.slug === slug);
  if (ex) {
    uebernimm(ex, neu);
  } else if (!devices.loading) {
    // Ein Gerät, das die Liste noch nicht kennt (eben dazugekommen). Vor dem
    // ersten Abruf legt load() den Stand über die Antwort.
    devices.list = [...devices.list, neu];
  }
}

function beiWeg(instanz: string): void {
  const slug = slugAusInstanz(instanz);
  if (!slug) return;
  stand.set(slug, { s: null, nr: ++standNr });
  offen.delete(slug);
  if (devices.list.some((d) => d.slug === slug)) devices.list = devices.list.filter((d) => d.slug !== slug);
}

// applyDevices merged die frische Liste IN-PLACE in den bestehenden Store:
// vorhandene Geräte behalten Objekt-Identität (nur Felder werden
// aktualisiert), die Array-Reihenfolge bleibt. So kein Voll-Re-Render/
// Zappeln der Tabelle bei jedem Live-Sensorwert.
//
// Protokoll-Stände, die während des Abrufs kamen (Nummer > ab), liegen
// darüber: die Antwort kann dahinter liegen. Ein Gerät, das nur so ein Stand
// kennt (kam während des Abrufs dazu), bleibt in der Liste.
function applyDevices(incoming: Device[], ab: number): void {
  const kommt = new Set(incoming.map((d) => d.slug));
  const alle: Device[] = [];
  for (const d of incoming) {
    const m = stand.get(d.slug);
    if (!m || m.nr <= ab) alle.push(d);
    else if (m.s) alle.push(ausStand(d.slug, m.s));
  }
  for (const [slug, m] of stand) if (!kommt.has(slug) && m.nr > ab && m.s) alle.push(ausStand(slug, m.s));

  const cur = devices.list;
  const byId = new Map(cur.map((d) => [d.slug, d]));
  const next = alle.map((d) => {
    const ex = byId.get(d.slug);
    if (ex) {
      uebernimm(ex, d);
      return ex;
    }
    return d;
  });
  const same = next.length === cur.length && next.every((d, i) => d === cur[i]);
  if (!same) devices.list = next;
}

// Nur das gescheiterte Feld: ein anderes, eben gespeichertes bleibt offen.
function vergissOffen(slug: string, feld: "label" | "area"): void {
  const o = offen.get(slug);
  if (!o) return;
  delete o[feld];
  if (o.label === undefined && o.area === undefined) offen.delete(slug);
}

function merkeOffen(slug: string, w: { label?: string; area?: string }): void {
  offen.set(slug, { ...offen.get(slug), ...w, bis: Date.now() + OFFEN_MS });
}

// renameDevice setzt den v2-eigenen Anzeigenamen (Override). Leerer Name =
// Override entfernen (Quell-Name gilt wieder). Optimistisch; bei Fehler
// die Liste neu holen (zeigt wieder, was gilt) und den Fehler WEITERWERFEN
// -- der Aufrufer meldet ihn am Ort. Bis 21.10.2026 landete er in
// devices.error, und das gleich folgende load() leerte ihn wieder: die
// Umbenennung scheiterte ohne sichtbare Meldung (Repo-Prüfung, zweite
// Runde).
export async function renameDevice(slug: string, label: string): Promise<void> {
  const d = devices.list.find((x) => x.slug === slug);
  if (d) d.label = label;
  merkeOffen(slug, { label });
  try {
    await postJSON(`/v2-api/iot/devices/${slug}/label`, { label });
  } catch (e) {
    vergissOffen(slug, "label");
    void load();
    throw e;
  }
}

// setDeviceArea setzt das freie Bereichs-Feld (Serverraum, Keller, …).
// Fehler wie bei renameDevice: neu holen, weiterwerfen.
export async function setDeviceArea(slug: string, area: string): Promise<void> {
  const d = devices.list.find((x) => x.slug === slug);
  if (d) d.area = area;
  merkeOffen(slug, { area });
  try {
    await postJSON(`/v2-api/iot/devices/${slug}/area`, { area });
  } catch (e) {
    vergissOffen(slug, "area");
    void load();
    throw e;
  }
}

// ── Geräte-Verlauf ───────────────────────────────────────────────────
// Persistierter Verlauf je Entität (Tabelle entity_history): Flanken
// sofort, Messwerte mit Totband. Grundlage des Verlauf-Overlays
// (DeviceHistory.svelte). Wire-Form identisch zu home-dash.
export interface HistPoint {
  ts: string;
  state: string;
  num?: number;
}

export interface HistEntity {
  slug: string;
  name: string;
  kind: string;
  unit: string;
  deviceClass: string;
  extId: string;
  prior: HistPoint | null; // Startzustand am linken Fensterrand
  points: HistPoint[];
}

export interface DeviceHistoryData {
  device: { slug: string; name: string; label: string; area: string };
  from: string;
  to: string;
  entities: HistEntity[];
}

export async function fetchDeviceHistory(slug: string, from: Date, to: Date): Promise<DeviceHistoryData> {
  const qs = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
  return getJSON<DeviceHistoryData>(`/v2-api/iot/devices/${slug}/history?${qs}`);
}
